import type { ServiceEvent, ServiceEventLineItemCategory } from "@/lib/service/types";
import {
  lineItemAmountCents,
  SERVICE_EVENT_LINE_ITEM_CATEGORY_LABELS,
} from "@/lib/service/eventLineItemMath";
import {
  dangerBtnClass,
  fmtCents,
  ghostBtnClass,
  inputClass,
  primaryBtnClass,
} from "./serviceShared";
import { useServiceEventLineItems } from "./useServiceEventLineItems";

const CATEGORIES: ServiceEventLineItemCategory[] = ["labor", "materials", "travel", "other"];

export function ServiceEventLineItemsPanel({ event }: { event: ServiceEvent }) {
  const li = useServiceEventLineItems(event);

  return (
    <div className="mt-4 border-t border-gray-100 pt-4 dark:border-zinc-800">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Actual line items</h4>
      <p className="mb-2 text-xs text-gray-500 dark:text-zinc-400">
        Manual actuals at job wrap-up — integer cents math only; no tax or margin engine.
      </p>

      {li.error ? <p className="mb-2 text-sm text-red-700 dark:text-red-300">{li.error}</p> : null}

      {li.busy && li.lines.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">Loading line items…</p>
      ) : null}

      {li.lines.length > 0 ? (
        <ul className="mb-3 divide-y divide-gray-100 dark:divide-zinc-800">
          {li.lines.map((line) => (
            <li key={line.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
              <div>
                <div className="font-medium text-gray-900 dark:text-zinc-100">
                  {SERVICE_EVENT_LINE_ITEM_CATEGORY_LABELS[line.category]} · {line.label}
                </div>
                <div className="text-xs text-gray-500 dark:text-zinc-400">
                  {line.amount_cents != null ? (
                    <>Flat {fmtCents(line.amount_cents)}</>
                  ) : (
                    <>
                      × {line.quantity}
                      {line.unit_cents != null ? ` @ ${fmtCents(line.unit_cents)}` : ""}
                    </>
                  )}{" "}
                  · Line total {fmtCents(lineItemAmountCents(line))}
                </div>
              </div>
              {li.editable ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    className={ghostBtnClass}
                    disabled={li.actionBusy}
                    onClick={() => li.editLine(line)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className={dangerBtnClass}
                    disabled={li.actionBusy}
                    onClick={() => void li.removeLine(line.id)}
                  >
                    Remove
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : !li.busy ? (
        <p className="mb-2 text-sm text-gray-500 dark:text-zinc-400">No line items yet.</p>
      ) : null}

      {li.lines.length > 0 || li.subtotalCents > 0 ? (
        <div className="mb-3 space-y-1 text-sm text-gray-800 dark:text-zinc-200">
          <p className="font-semibold">Actuals subtotal: {fmtCents(li.subtotalCents)}</p>
          {event.quoted_amount_cents != null ? (
            <p className="text-xs text-gray-600 dark:text-zinc-400">
              Quoted (read-only): {fmtCents(event.quoted_amount_cents)}
              {" · "}
              Delta: {fmtCents(li.subtotalCents - event.quoted_amount_cents)}
            </p>
          ) : null}
        </div>
      ) : null}

      {li.editable ? (
        <div className="space-y-2 rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-zinc-700 dark:bg-zinc-900/30">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
            {li.draft.id ? "Edit line" : "Add line"}
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <select
              className={inputClass}
              value={li.draft.category}
              onChange={(e) =>
                li.setDraft((d) => ({ ...d, category: e.target.value as ServiceEventLineItemCategory }))
              }
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {SERVICE_EVENT_LINE_ITEM_CATEGORY_LABELS[cat]}
                </option>
              ))}
            </select>
            <input
              className={`${inputClass} sm:col-span-2`}
              placeholder="Label"
              value={li.draft.label}
              onChange={(e) => li.setDraft((d) => ({ ...d, label: e.target.value }))}
            />
            <input
              className={inputClass}
              placeholder="Qty"
              value={li.draft.quantity}
              onChange={(e) => li.setDraft((d) => ({ ...d, quantity: e.target.value }))}
              disabled={li.draft.pricing_mode === "flat"}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <label className="inline-flex items-center gap-1.5">
              <input
                type="radio"
                name={`pricing-${event.id}`}
                checked={li.draft.pricing_mode === "unit"}
                onChange={() => li.setDraft((d) => ({ ...d, pricing_mode: "unit" }))}
              />
              Unit price
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input
                type="radio"
                name={`pricing-${event.id}`}
                checked={li.draft.pricing_mode === "flat"}
                onChange={() => li.setDraft((d) => ({ ...d, pricing_mode: "flat" }))}
              />
              Flat amount
            </label>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {li.draft.pricing_mode === "unit" ? (
              <input
                className={inputClass}
                placeholder="Unit price (USD)"
                value={li.draft.unit_dollars}
                onChange={(e) => li.setDraft((d) => ({ ...d, unit_dollars: e.target.value }))}
              />
            ) : (
              <input
                className={inputClass}
                placeholder="Flat amount (USD)"
                value={li.draft.amount_dollars}
                onChange={(e) => li.setDraft((d) => ({ ...d, amount_dollars: e.target.value }))}
              />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={primaryBtnClass}
              disabled={li.actionBusy}
              onClick={() => void li.saveDraft()}
            >
              {li.actionBusy ? "Saving…" : li.draft.id ? "Update line" : "Add line"}
            </button>
            {li.draft.id ? (
              <button
                type="button"
                className={ghostBtnClass}
                disabled={li.actionBusy}
                onClick={li.cancelEdit}
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Move the job to in progress or complete to add actual line items.
        </p>
      )}
    </div>
  );
}
